import React, { useState } from 'react';
import {
  FiCheck as Check,
  FiX as X,
  FiAlertTriangle as AlertTriangle,
  FiMail as Mail,
  FiMessageSquare as Slack,
  FiMonitor as InApp,
  FiXCircle as XCircle,
  FiZap as Zap,
} from 'react-icons/fi';
import { AlertPreference, AlertRuleItem, CategoryConfigItem, PresetConfig } from './types';
import { CATEGORY_CONFIG, EMAIL_FREQUENCIES, PRESET_ICONS, SEVERITY_COLORS } from './constants';

interface PreferenceConfigModalProps {
  config: CategoryConfigItem;
  preference: AlertPreference;
  onSave: (updates: Record<string, unknown>) => void;
  onClose: () => void;
}

export const PreferenceConfigModal: React.FC<PreferenceConfigModalProps> = ({ config, preference, onSave, onClose }) => {
  const [inApp, setInApp] = useState(preference.in_app_enabled);
  const [email, setEmail] = useState(preference.email_enabled);
  const [slack, setSlack] = useState(preference.slack_enabled);
  const [frequency, setFrequency] = useState(preference.email_frequency || 'immediate');

  const handleSave = () => {
    onSave({
      in_app_enabled: inApp,
      email_enabled: email,
      slack_enabled: slack,
      email_frequency: frequency,
    });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75 transition-opacity" onClick={onClose} onKeyDown={(e) => { if (['Enter', ' '].includes(e.key)) onClose(); }} role="presentation" />

        <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
          <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Configure: {config.name} Notifications
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="px-6 py-4 space-y-6">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {config.description}
            </div>

            <div className="space-y-2">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Delivery Channels
              </span>
              <div className="space-y-2">
                <button
                  onClick={() => setInApp(!inApp)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                    inApp
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                >
                  <span className={`p-2 rounded-lg ${inApp ? 'bg-blue-100 dark:bg-blue-900/50' : 'bg-gray-100 dark:bg-gray-600'}`}>
                    <InApp className={`w-4 h-4 ${inApp ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`} />
                  </span>
                  <div className="flex-1">
                    <div className={`text-sm font-medium ${inApp ? 'text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'}`}>
                      In-App Notifications
                    </div>
                  </div>
                  {inApp && <Check className="w-5 h-5 text-blue-600 dark:text-blue-400" />}
                </button>

                <button
                  onClick={() => setEmail(!email)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                    email
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                >
                  <span className={`p-2 rounded-lg ${email ? 'bg-blue-100 dark:bg-blue-900/50' : 'bg-gray-100 dark:bg-gray-600'}`}>
                    <Mail className={`w-4 h-4 ${email ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`} />
                  </span>
                  <div className="flex-1">
                    <div className={`text-sm font-medium ${email ? 'text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'}`}>
                      Email
                    </div>
                  </div>
                  {email && <Check className="w-5 h-5 text-blue-600 dark:text-blue-400" />}
                </button>

                <button
                  onClick={() => setSlack(!slack)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                    slack
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                >
                  <span className={`p-2 rounded-lg ${slack ? 'bg-blue-100 dark:bg-blue-900/50' : 'bg-gray-100 dark:bg-gray-600'}`}>
                    <Slack className={`w-4 h-4 ${slack ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`} />
                  </span>
                  <div className="flex-1">
                    <div className={`text-sm font-medium ${slack ? 'text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'}`}>
                      Slack
                    </div>
                  </div>
                  {slack && <Check className="w-5 h-5 text-blue-600 dark:text-blue-400" />}
                </button>
              </div>
            </div>

            {email && (
              <div className="space-y-2">
                <label htmlFor="email-frequency" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Email Frequency
                </label>
                <select id="email-frequency"
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {EMAIL_FREQUENCIES.map((freq) => (
                    <option key={freq.value} value={freq.value}>
                      {freq.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Check className="w-4 h-4" />
              Save Configuration
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface AlertRuleModalProps {
  rule: AlertRuleItem | null;
  onSave: (data: Record<string, unknown>) => void;
  onClose: () => void;
}

export const AlertRuleModal: React.FC<AlertRuleModalProps> = ({ rule, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: rule?.name || '',
    description: rule?.description || '',
    metric: rule?.metric || 'error_rate',
    operator: rule?.operator || 'gt',
    threshold: rule?.threshold || '',
    anomaly_sensitivity: rule?.anomaly_sensitivity || 2,
    scope_type: rule?.scope_type || 'all',
    scope_value: rule?.scope_value || '',
    severity: rule?.severity || 'warning',
    channel_ids: rule?.channel_ids || [],
    duration_seconds: rule?.duration_seconds || 300,
    is_enabled: rule?.is_enabled ?? true
  });

  const metrics = [
    { value: 'error_rate', label: 'Error Rate' },
    { value: 'latency_p50', label: 'Latency (P50)' },
    { value: 'latency_p95', label: 'Latency (P95)' },
    { value: 'latency_p99', label: 'Latency (P99)' },
    { value: 'latency_avg', label: 'Latency (Average)' },
    { value: 'cost_per_hour', label: 'Cost per Hour' },
    { value: 'cost_per_day', label: 'Cost per Day' },
    { value: 'tokens_per_hour', label: 'Tokens per Hour' },
    { value: 'pass_rate', label: 'Pass Rate' },
    { value: 'quality_score', label: 'Quality Score' },
    { value: 'safety_critical_count', label: 'Safety Critical Count' },
    { value: 'safety_high_count', label: 'Safety High Count' },
    { value: 'request_count', label: 'Request Count' },
    { value: 'cache_hit_rate', label: 'Cache Hit Rate' },
  ];

  const operators = [
    { value: 'gt', label: 'Greater than' },
    { value: 'lt', label: 'Less than' },
    { value: 'gte', label: 'Greater than or equal' },
    { value: 'lte', label: 'Less than or equal' },
    { value: 'eq', label: 'Equals' },
    { value: 'anomaly', label: 'Anomaly detection' }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-lg mx-4 border border-gray-200 dark:border-gray-700 max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            {rule ? 'Edit Alert Rule' : 'Create Alert Rule'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-white">
            <XCircle className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label htmlFor="rule-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Rule Name
            </label>
            <input id="rule-name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., High Error Rate Alert"
              className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description (optional)
            </label>
            <textarea id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="What does this alert monitor?"
              className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="metric" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Metric
              </label>
              <select id="metric"
                value={formData.metric}
                onChange={(e) => setFormData({ ...formData, metric: e.target.value })}
                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {metrics.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="condition" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Condition
              </label>
              <select id="condition"
                value={formData.operator}
                onChange={(e) => setFormData({ ...formData, operator: e.target.value })}
                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {operators.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          {formData.operator === 'anomaly' ? (
            <div>
              <label htmlFor="anomaly-sensitivity-1-5" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Anomaly Sensitivity (1-5)
              </label>
              <input id="anomaly-sensitivity-1-5"
                type="range"
                min="1"
                max="5"
                step="0.5"
                value={formData.anomaly_sensitivity}
                onChange={(e) => setFormData({ ...formData, anomaly_sensitivity: Number.parseFloat(e.target.value) })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>Less sensitive</span>
                <span>{formData.anomaly_sensitivity}</span>
                <span>More sensitive</span>
              </div>
            </div>
          ) : (
            <div>
              <label htmlFor="threshold-value" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Threshold Value
              </label>
              <input id="threshold-value"
                type="number"
                step="0.01"
                value={formData.threshold}
                onChange={(e) => setFormData({ ...formData, threshold: Number.parseFloat(e.target.value) })}
                placeholder="e.g., 0.1 for 10%"
                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="scope" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Scope
              </label>
              <select id="scope"
                value={formData.scope_type}
                onChange={(e) => setFormData({ ...formData, scope_type: e.target.value })}
                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All resources</option>
                <option value="prompt">Specific prompt</option>
                <option value="agent">Specific agent</option>
                <option value="model">Specific model</option>
              </select>
            </div>

            <div>
              <label htmlFor="severity" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Severity
              </label>
              <select id="severity"
                value={formData.severity}
                onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="critical">Critical</option>
                <option value="warning">Warning</option>
                <option value="info">Info</option>
              </select>
            </div>
          </div>

          {formData.scope_type !== 'all' && (
            <div>
              <label htmlFor="scope-value" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {formData.scope_type === 'prompt' ? 'Prompt ID' :
                 formData.scope_type === 'agent' ? 'Agent Name' : 'Model ID'}
              </label>
              <input id="scope-value"
                type="text"
                value={formData.scope_value}
                onChange={(e) => setFormData({ ...formData, scope_value: e.target.value })}
                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          )}

          <div>
            <label htmlFor="persistence-seconds-before-alerting" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Persistence (seconds before alerting)
            </label>
            <input id="persistence-seconds-before-alerting"
              type="number"
              value={formData.duration_seconds}
              onChange={(e) => setFormData({ ...formData, duration_seconds: Number.parseInt(e.target.value) })}
              className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">Condition must persist for this duration before firing</p>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-white rounded transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors"
            >
              {rule ? 'Save Changes' : 'Create Rule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface PresetModalProps {
  category: string;
  categoryPresets: PresetConfig[];
  onApplyMultiple: (presetNames: string[]) => void;
  onClose: () => void;
}

export const PresetModal: React.FC<PresetModalProps> = ({ category, categoryPresets, onApplyMultiple, onClose }) => {
  const [selectedPresets, setSelectedPresets] = useState(
    categoryPresets.filter(p => p.recommended).map(p => p.name)
  );

  const categoryConfig = CATEGORY_CONFIG[category] || { name: 'Alerts' };

  const togglePreset = (presetName: string) => {
    setSelectedPresets(prev =>
      prev.includes(presetName)
        ? prev.filter(n => n !== presetName)
        : [...prev, presetName]
    );
  };

  const handleApplySelected = () => {
    onApplyMultiple(selectedPresets);
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl mx-4 border border-gray-200 dark:border-gray-700 shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2">
              <Zap className="w-5 h-5 text-purple-500" />
              Quick Setup - {categoryConfig.name}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Select alert templates to add for {categoryConfig.name}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-white">
            <XCircle className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-1">
          <div className="grid gap-3">
            {categoryPresets.map(preset => {
              const Icon = PRESET_ICONS[preset.name as keyof typeof PRESET_ICONS] || AlertTriangle;
              const severity = SEVERITY_COLORS[preset.severity as keyof typeof SEVERITY_COLORS] || SEVERITY_COLORS.warning;
              const isSelected = selectedPresets.includes(preset.name);

              return (
                <button
                  key={preset.name}
                  onClick={() => togglePreset(preset.name)}
                  className={`p-4 rounded-lg border text-left transition-all ${
                    isSelected
                      ? `${severity.border} ${severity.bg} ring-2 ring-blue-500`
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-gray-50 dark:bg-gray-900/30'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      isSelected
                        ? 'bg-blue-500 border-blue-500'
                        : 'border-gray-300 dark:border-gray-600'
                    }`}>
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </div>

                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      isSelected ? severity.bg : 'bg-gray-100 dark:bg-gray-800'
                    }`}>
                      <Icon className={`w-5 h-5 ${isSelected ? severity.text : 'text-gray-400'}`} />
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900 dark:text-white">{preset.label}</span>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${severity.bg} ${severity.text}`}>
                          {preset.severity}
                        </span>
                        {preset.recommended && (
                          <span className="px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full">
                            Recommended
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{preset.description}</div>
                      <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        {preset.metric} {preset.condition}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {selectedPresets.length} of {categoryPresets.length} selected
          </span>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-white rounded transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleApplySelected}
              disabled={selectedPresets.length === 0}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded transition-colors flex items-center gap-2"
            >
              <Check className="w-4 h-4" />
              Apply Selected ({selectedPresets.length})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
